import {
  humanizeFeatureCode,
  isModuleRootKind,
  LOCK_PLACEMENT_BY_KIND,
  WHERE_BY_KIND,
  type FeatureSurfaceKindCode,
} from "@/v2-common/featureSurface/featureSurface.constants"
import { FEATURE_AVAILABILITY_TYPE_CODE } from "@/v2-common/featureSurface/featureSurface.constants"
import type { BadgeVariant } from "@/v2-backoffice-client/components/badge"
import type { DotProps } from "@/v2-backoffice-client/components/dot"
import { FEATURE_SURFACE_CHANGE_KIND } from "@/v2-backoffice-client/license/changes/license.changes.constants"
import type {
  LicenseDecision,
  LicenseDecisionSubject,
} from "@/v2-backoffice-client/license/changes/license.changes.decision.types"
import type { FeatureSurfaceChangesQuery } from "@/v2-backoffice-client/license/changes/__types__/license.changes.query"

export type LicenseChange =
  FeatureSurfaceChangesQuery["getFeatureSurfaceChanges"][number]

/**
 * A capability still waits on an operator while it is live, free for
 * everyone and no one has said that is intended: it reached every customer
 * on arrival. A decision is one of two things — "Free for everyone",
 * recorded on the row, or "Package-only", which locks it — so being
 * included in a package while free is not one: the package decides nothing
 * until the capability is package-only.
 */
export function isUndecided(change: LicenseChange) {
  return (
    change.IsInCatalog &&
    change.FeatureAvailabilityTypeCode ===
      FEATURE_AVAILABILITY_TYPE_CODE.availableForAll &&
    !change.FreeConfirmedAt
  )
}

/**
 * The features (not rows: a moved feature has two) still waiting on a
 * decision — the tab's count.
 */
export function countUndecidedFeatures(changes: readonly LicenseChange[]) {
  return new Set(
    changes.filter(isUndecided).map((change) => change.SurfaceCode),
  ).size
}

/** Seeded with its code as its name: the text is not authored yet. */
export function isPlaceholderName(change: LicenseChange) {
  return change.Name === change.SurfaceCode
}

export function isLockedChange(change: LicenseChange) {
  return (
    change.FeatureAvailabilityTypeCode ===
    FEATURE_AVAILABILITY_TYPE_CODE.lockedWithinPackage
  )
}

/**
 * The feature's name as an operator should read it: the authored text, or
 * — for a module's own surface, which has none yet — the module's name.
 */
export function displayNameOf(change: LicenseChange) {
  if (!isPlaceholderName(change)) return change.Name
  if (isModuleRootKind(change.KindCode)) return moduleNameOf(change)
  return humanizeFeatureCode(change.Name)
}

/**
 * The module's name as an operator should read it: its authored root text,
 * or the code's module segment in words ("HousingUnit" → "Housing unit")
 * while nobody has written one.
 */
export function moduleNameOf(change: LicenseChange) {
  return change.ModuleName === change.ModuleCode
    ? humanizeFeatureCode(change.ModuleCode)
    : change.ModuleName
}

/** Where in Fusion the feature lives, in plain words. */
export function whereOf(change: LicenseChange) {
  const describe = WHERE_BY_KIND[change.KindCode as FeatureSurfaceKindCode]
  const moduleName = moduleNameOf(change)
  return describe ? describe(moduleName) : moduleName
}

/**
 * What locking this surface does in Fusion, and what it takes with it: the
 * sentence an operator reads before choosing package-only.
 */
export function lockPlacementOf(change: LicenseChange) {
  const name = displayNameOf(change)
  const parent = change.ParentName ?? moduleNameOf(change)
  const describe =
    LOCK_PLACEMENT_BY_KIND[change.KindCode as FeatureSurfaceKindCode]
  const placement = describe
    ? describe(name, parent)
    : `Locks "${name}" in ${parent}.`
  if (change.InsideCount === 0) return placement
  return `${placement} The ${change.InsideCount} ${change.InsideCount === 1 ? "surface" : "surfaces"} inside it (tabs, sections, tools) become unreachable too.`
}

/** What happened, in one word. */
export function changeWordOf(change: LicenseChange) {
  switch (change.ChangeKindCode) {
    case FEATURE_SURFACE_CHANGE_KIND.added:
      return "New"
    case FEATURE_SURFACE_CHANGE_KIND.removed:
      return "Removed"
    case FEATURE_SURFACE_CHANGE_KIND.moved:
      return "Moved"
    default:
      return change.ChangeKindName
  }
}

/**
 * The decision as the panel's form shows it: null while the feature is
 * free for everyone and no one has said so.
 */
export function decisionOf(change: LicenseChange): LicenseDecision | null {
  if (!change.IsInCatalog) return null
  if (isLockedChange(change)) return "packages"
  return change.FreeConfirmedAt ? "everyone" : null
}

/** The change-feed row as the shared decision form reads it. */
export function capabilityDecisionSubjectOf(
  change: LicenseChange,
): LicenseDecisionSubject {
  return {
    code: change.SurfaceCode,
    name: displayNameOf(change),
    isLocked: isLockedChange(change),
    whoHasIt: whoHasIt(change),
    packageNames: packagesOf(change),
    pickedPackageIds: (change.FeatureGroupIds ?? "").split(",").filter(Boolean),
    lockSentence: lockPlacementOf(change),
    decision: decisionOf(change),
    freeConfirmedBy: change.FreeConfirmedByUserName,
    freeConfirmedAt: change.FreeConfirmedAt,
    canUndecide: true,
    featureCodeLabel: "WhatsNew",
  }
}

/**
 * The Decision column and the status pills: what has been decided about a
 * capability, in the words the decision itself uses, so a decided row says
 * which way it went rather than only that it did.
 */
export const DECISION_STATUS = {
  needsDecision: "Needs a decision",
  everyone: "Free for everyone",
  packages: "Package-only",
  removed: "Removed from Fusion",
} as const

export type LicenseChangeStatus =
  (typeof DECISION_STATUS)[keyof typeof DECISION_STATUS]

export function statusOf(change: LicenseChange): LicenseChangeStatus {
  if (!change.IsInCatalog) return DECISION_STATUS.removed
  if (isLockedChange(change)) return DECISION_STATUS.packages
  return isUndecided(change)
    ? DECISION_STATUS.needsDecision
    : DECISION_STATUS.everyone
}

/**
 * The hue each decision state wears, in the Decision column and the panel
 * alike: amber asks for the operator, green is open, a firm grey is the
 * lock (settled, nothing to do), a faint grey is gone.
 */
export const DECISION_STATUS_DOT_VARIANT: Record<
  LicenseChangeStatus,
  DotProps["variant"]
> = {
  [DECISION_STATUS.needsDecision]: "warning",
  [DECISION_STATUS.everyone]: "success-1",
  [DECISION_STATUS.packages]: "secondary",
  [DECISION_STATUS.removed]: "muted",
}

/** The Change badge's hue per kind: a new capability is good news, a removed one a cleanup. */
export const CHANGE_BADGE_VARIANT: Record<number, BadgeVariant> = {
  [FEATURE_SURFACE_CHANGE_KIND.added]: "positive",
  [FEATURE_SURFACE_CHANGE_KIND.removed]: "critical",
  [FEATURE_SURFACE_CHANGE_KIND.moved]: "neutral",
}

/** The packages that include the capability, as a list. */
export function packagesOf(change: LicenseChange) {
  return splitPackageNames(change.FeatureGroupNames)
}

/** "Basic", "Basic or Standard", "Basic, Standard or Pro". */
export function joinOr(items: readonly string[]) {
  if (items.length <= 1) return items.join("")
  return `${items.slice(0, -1).join(", ")} or ${items[items.length - 1]}`
}

/**
 * The "Placed in" column's two buckets — the question "does any package
 * carry this capability?", asked the same way of a free capability (then
 * it is a placement for the day it is made package-only) and of a
 * package-only one (then it is who gets it). Grouping and filtering use the
 * bucket; the cell shows the names.
 */
export const PLACEMENT = {
  placed: "In a package",
  unplaced: "No package",
} as const

export type LicenseChangePlacement = (typeof PLACEMENT)[keyof typeof PLACEMENT]

export function placementOf(row: {
  FeatureGroupNames?: string | null
}): LicenseChangePlacement {
  return splitPackageNames(row.FeatureGroupNames).length > 0
    ? PLACEMENT.placed
    : PLACEMENT.unplaced
}

/** Who can use the capability right now, in one plain sentence. */
export function whoHasIt(change: LicenseChange) {
  if (!change.IsInCatalog) return "No one: it is gone from Fusion"
  return whoHasPackaged(isLockedChange(change), packagesOf(change))
}

/**
 * Who can use a feature right now, from its lock and its packages: the one
 * sentence both Feature access views, the feature panels and the layer
 * sheet say ("Everyone", "Customers with Basic or Standard", or the lock
 * nobody carries, which reaches staff only).
 */
export function whoHasPackaged(isLocked: boolean, packages: readonly string[]) {
  if (!isLocked) return "Everyone"
  return packages.length > 0
    ? `Customers with ${joinOr(packages)}`
    : "Nobody yet: package-only, and no package includes it"
}

/** "Basic, Standard" as a list, from the catalog's comma-joined string. */
export function splitPackageNames(names: string | null | undefined) {
  return (names ?? "").split(", ").filter(Boolean)
}

/** The release that shipped it, or where it stands if none has. */
export function releaseOf(change: LicenseChange) {
  if (change.IntroducedVersion) return change.IntroducedVersion
  if (change.IntroducedAt) return "Not released yet"
  return null
}

export type LicenseProvenanceLike = {
  IntroducedAt?: string | null
  IntroducedCommit?: string | null
  IntroducedIssue?: string | null
  IntroducedPullRequest?: number | null
  IntroducedVersion?: string | null
}

/** Whether the surface has any provenance to show. */
export function hasProvenance(provenance: LicenseProvenanceLike) {
  return Boolean(
    provenance.IntroducedAt ||
    provenance.IntroducedIssue ||
    provenance.IntroducedPullRequest ||
    provenance.IntroducedVersion,
  )
}
